import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Plus,
  Play,
  Star,
  Utensils,
  Leaf,
} from "lucide-react";

import { fetchMenu } from "../../actions/menuActions";
import { useCart } from "../../components/CartContext";

import "./css/HomeScreen.css";


/*
|--------------------------------------------------------------------------
| Internet / decorative images
|--------------------------------------------------------------------------
*/

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=1800&q=90";

const AMBIENCE_IMAGE =
  "https://images.unsplash.com/photo-1515003197210-e0cd71810b5f?auto=format&fit=crop&w=1800&q=90";

const GALLERY_IMAGE_1 =
  "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=1000&q=90";

const GALLERY_IMAGE_2 =
  "https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?auto=format&fit=crop&w=1000&q=90";


/*
|--------------------------------------------------------------------------
| Backend image helper
|--------------------------------------------------------------------------
*/

const getImageUrl = (image) => {

  if (!image) {
    return "";
  }

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `${
    image.startsWith("/") ? "" : "/"
  }${image}`;
};


function HomeScreen() {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    restaurantId,
  } = useParams();


  /*
  |--------------------------------------------------------------------------
  | IMPORTANT
  |--------------------------------------------------------------------------
  | Your reducer stores:
  |
  | state.menu.categories
  |
  | NOT:
  |
  | state.menu.menu
  |--------------------------------------------------------------------------
  */

  const {
    categories,
    loading,
    error,
  } = useSelector(
    (state) => state.menu
  );


  const {
    addToCart,
    cartCount,
  } = useCart();


  const [
    activeCategory,
    setActiveCategory,
  ] = useState(null);


  const [popularPage, setPopularPage] = useState(0);

  const [
    currentCategory,
    setCurrentCategory,
  ] = useState(0);


  /*
  |--------------------------------------------------------------------------
  | Fetch menu
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!restaurantId) {
      return;
    }

    dispatch(
      fetchMenu(restaurantId)
    );

  }, [
    dispatch,
    restaurantId,
  ]);


  /*
  |--------------------------------------------------------------------------
  | Sort backend categories
  |--------------------------------------------------------------------------
  */

  const sortedCategories = useMemo(() => {

    if (
      !Array.isArray(categories)
    ) {
      return [];
    }

    return [...categories]

      .filter(
        (category) =>
          category.is_active !== false
      )

      .sort(
        (a, b) =>
          Number(
            a.display_order || 0
          ) -
          Number(
            b.display_order || 0
          )
      )

      .map((category) => ({

        ...category,

        items: [
          ...(category.items || []),
        ]

          .filter(
            (item) =>
              item.is_available !== false
          )

          .sort(
            (a, b) =>
              Number(
                a.display_order || 0
              ) -
              Number(
                b.display_order || 0
              )
          ),

      }));

  }, [
    categories,
  ]);


  /*
  |--------------------------------------------------------------------------
  | POPULAR DISHES
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | Take display_order 1, 2 and 3
  | from EVERY category.
  |
  | Example:
  |
  | Starters
  |   1
  |   2
  |   3
  |
  | Tandoor
  |   1
  |   2
  |   3
  |
  | Biryani
  |   1
  |   2
  |   3
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!sortedCategories.length) {
      setActiveCategory(null);
      return;
    }
    setActiveCategory((previous) =>
      sortedCategories.some((category) => category.id === previous)
        ? previous
        : sortedCategories[0].id
    );
  }, [sortedCategories]);

  const popularItems = useMemo(() => {

    const result = [];

    sortedCategories.forEach(
      (category) => {

        const firstThree =
          [...category.items]

            .sort(
              (a, b) =>
                Number(
                  a.display_order || 0
                ) -
                Number(
                  b.display_order || 0
                )
            )

            .slice(0, 3);


        firstThree.forEach(
          (item) => {

            result.push({

              ...item,

              categoryName:
                category.name,

            });

          }
        );

      }
    );


    return result;

  }, [
    sortedCategories,
  ]);


  // Six popular dishes at a time: three columns, two rows on mobile.
  const popularPageCount = Math.max(1, Math.ceil(popularItems.length / 6));
  useEffect(() => {
    setPopularPage(0);
  }, [popularItems.length]);
  useEffect(() => {
    if (popularPageCount < 2) return undefined;
    const timer = window.setInterval(() => {
      setPopularPage((page) => (page + 1) % popularPageCount);
    }, 3500);
    return () => window.clearInterval(timer);
  }, [popularPageCount]);
  const visiblePopularItems = popularItems.slice(popularPage * 6, popularPage * 6 + 6);

  /*
  |--------------------------------------------------------------------------
  | Category slider
  |--------------------------------------------------------------------------
  */

  const visibleCategories =
    sortedCategories.slice(
      currentCategory,
      currentCategory + 5
    );


  const handleNextCategories = () => {

    if (
      currentCategory + 5 <
      sortedCategories.length
    ) {

      setCurrentCategory(
        currentCategory + 1
      );

    }

  };


  const handlePreviousCategories = () => {

    if (
      currentCategory > 0
    ) {

      setCurrentCategory(
        currentCategory - 1
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | Scroll to menu
  |--------------------------------------------------------------------------
  */

  const scrollToMenu = () => {

    document
      .getElementById("theme8-menu")
      ?.scrollIntoView({
        behavior: "smooth",
      });

  };


  /*
  |--------------------------------------------------------------------------
  | Add to cart
  |--------------------------------------------------------------------------
  */

  const handleAddToCart = (
    item
  ) => {

    addToCart(item);

  };


  /*
  |--------------------------------------------------------------------------
  | Category selection
  |--------------------------------------------------------------------------
  */

  const handleCategoryClick = (
    category
  ) => {

    setActiveCategory(
      category.id
    );

    setTimeout(() => {

      document
        .getElementById(
          "theme8-menu"
        )
        ?.scrollIntoView({
          behavior: "smooth",
        });

    }, 50);

  };


  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {

    return (
      <main className="theme8-loading">

        <div className="theme8-loader" />

        <p>
          Preparing your table...
        </p>

      </main>
    );

  }


  /*
  |--------------------------------------------------------------------------
  | Error
  |--------------------------------------------------------------------------
  */

  if (error) {

    return (
      <main className="theme8-error">

        <span>
          MENU
        </span>

        <h2>
          Unable to load menu
        </h2>

        <p>
          {typeof error === "string"
            ? error
            : "Please try again."}
        </p>

        <button
          type="button"
          onClick={() =>
            dispatch(
              fetchMenu(
                restaurantId
              )
            )
          }
        >
          Try Again
        </button>

      </main>
    );

  }


  return (
    <main className="theme8-home">


      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="theme8-hero">

        <div className="theme8-hero-yellow" />

        <div className="theme8-hero-content">

          <div className="theme8-hero-copy">

            <span className="theme8-eyebrow">
              MODERN INDIAN CUISINE
            </span>

            <h1>
              Taste the
              <strong>
                Extraordinary.
              </strong>
            </h1>

            <p>
              Authentic flavours.
              Modern presentation.
              <br />
              A dining experience crafted
              for the new generation.
            </p>

            <div className="theme8-hero-actions">

              <button
                type="button"
                className="theme8-yellow-button"
                onClick={scrollToMenu}
              >
                Explore Menu
                <ArrowRight size={18} />
              </button>

              <button
                type="button"
                className="theme8-story-button"
                onClick={() =>
                  document
                    .getElementById(
                      "theme8-gallery"
                    )
                    ?.scrollIntoView({
                      behavior: "smooth",
                    })
                }
              >

                <span className="theme8-play">

                  <Play
                    size={15}
                    fill="currentColor"
                  />

                </span>

                Watch Our Story

              </button>

            </div>

          </div>


          {/* HERO IMAGE */}

          <div className="theme8-hero-food">

            <div className="theme8-food-ring">

              <div className="theme8-food-image-wrap">

                <img
                  src={HERO_IMAGE}
                  alt="Indian cuisine"
                />

              </div>

            </div>

          </div>


          {/* HERO STATS */}

          <div className="theme8-hero-stats">

            <div className="theme8-stat">

              <Utensils />

              <div>

                <strong>
                  10+
                </strong>

                <span>
                  Expert Chefs
                </span>

              </div>

            </div>


            <div className="theme8-stat">

              <Star />

              <div>

                <strong>
                  50+
                </strong>

                <span>
                  Delicious Dishes
                </span>

              </div>

            </div>


            <div className="theme8-stat">

              <Leaf />

              <div>

                <strong>
                  100%
                </strong>

                <span>
                  Fresh Ingredients
                </span>

              </div>

            </div>

          </div>

        </div>


        {/* SOCIAL */}

        <div className="theme8-social">

          <a
            href="#!"
            onClick={(e) =>
              e.preventDefault()
            }
          >
            f
          </a>

          <a
            href="#!"
            onClick={(e) =>
              e.preventDefault()
            }
          >
            ◎
          </a>

          <a
            href="#!"
            onClick={(e) =>
              e.preventDefault()
            }
          >
            𝕏
          </a>

          <a
            href="#!"
            onClick={(e) =>
              e.preventDefault()
            }
          >
            ◉
          </a>

        </div>

      </section>


      {/* =====================================================
          CATEGORIES
      ====================================================== */}

      <section className="theme8-specialties">

        <div className="theme8-section-intro">

          <span>
            OUR
          </span>

          <h2>
            <strong>
              Specialties
            </strong>
          </h2>

          <div className="theme8-small-line" />

          <p>
            Explore our most loved
            categories crafted with
            authentic Indian flavours.
          </p>

        </div>


        <div className="theme8-category-slider">

          <button
            type="button"
            className="theme8-slider-button"
            onClick={
              handlePreviousCategories
            }
            disabled={
              currentCategory === 0
            }
          >
            <ChevronLeft />
          </button>


          <div className="theme8-category-track">

            {visibleCategories.map(
              (category) => {

                /*
                |--------------------------------------------------------------------------
                | Use category image first.
                | If category doesn't have image,
                | use first menu item image.
                |--------------------------------------------------------------------------
                */

                const categoryImage =
                  getImageUrl(
                    category.image ||
                    category.items?.[0]?.image
                  );


                return (

                  <button
                    type="button"
                    key={category.id}
                    className={`theme8-category-card ${
                      activeCategory ===
                      category.id
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      handleCategoryClick(
                        category
                      )
                    }
                  >

                    <div className="theme8-category-image">

                      {categoryImage ? (

                        <img
                          src={
                            categoryImage
                          }
                          alt={
                            category.name
                          }
                        />

                      ) : (

                        <Utensils />

                      )}

                    </div>


                    <div className="theme8-category-info">

                      <h3>
                        {category.name}
                      </h3>

                      <span>
                        {
                          category.items
                            ?.length || 0
                        }{" "}
                        Items
                      </span>

                    </div>

                  </button>

                );

              }
            )}

          </div>


          <button
            type="button"
            className="theme8-slider-button"
            onClick={
              handleNextCategories
            }
            disabled={
              currentCategory + 5 >=
              sortedCategories.length
            }
          >
            <ChevronRight />
          </button>

        </div>

      </section>


      {/* =====================================================
          POPULAR DISHES
      ====================================================== */}

      <section className="theme8-popular">

        <div className="theme8-popular-header">

          <div>

            <span className="theme8-eyebrow">
              CHEF'S CHOICE
            </span>

            <h2>
              Popular
              <strong>
                Dishes
              </strong>
            </h2>

            <div className="theme8-small-line" />

            <p>
              Three signature dishes
              from every category.
            </p>

          </div>

          <button
            type="button"
            className="theme8-outline-button"
            onClick={scrollToMenu}
          >
            View Full Menu
            <ArrowRight size={17} />
          </button>

        </div>


        {/* =================================================
            3 ITEMS FROM EVERY CATEGORY
        ================================================= */}

        <div className="theme8-popular-grid">

          {visiblePopularItems.map(
            (item) => {

              const image =
                getImageUrl(
                  item.image
                );


              return (

                <article
                  className="theme8-dish-card"
                  key={`${item.categoryName}-${item.id}`}
                >

                  <div className="theme8-dish-image">

                    {image ? (

                      <img
                        src={image}
                        alt={item.name}
                      />

                    ) : (

                      <div className="theme8-dish-placeholder">
                        <Utensils />
                      </div>

                    )}

                    <span className="theme8-dish-category">
                      {item.categoryName}
                    </span>

                  </div>


                  <div className="theme8-dish-content">

                    <h3>
                      {item.name}
                    </h3>

                    <p>
                      {item.description ||
                        "Authentic Indian flavours prepared fresh for your table."}
                    </p>


                    <div className="theme8-dish-bottom">

                      <strong>
                        ₹
                        {Number(
                          item.price || 0
                        ).toFixed(0)}
                      </strong>

                      <button
                        type="button"
                        onClick={() =>
                          handleAddToCart(
                            item
                          )
                        }
                      >

                        <Plus size={20} />

                        <span>
                          Add
                        </span>

                      </button>

                    </div>

                  </div>

                </article>

              );

            }
          )}

        </div>

      </section>


      {/* =====================================================
          GALLERY
      ====================================================== */}

      <section
        id="theme8-gallery"
        className="theme8-gallery"
      >

        <div className="theme8-gallery-main">

          <img
            src={AMBIENCE_IMAGE}
            alt="Restaurant ambience"
          />

          <div className="theme8-gallery-overlay">

            <button
              type="button"
              className="theme8-gallery-play"
            >
              <Play
                size={20}
                fill="currentColor"
              />
            </button>

            <span>
              OUR AMBIENCE
            </span>

            <strong>
              A table worth
              remembering.
            </strong>

          </div>

        </div>


        <div className="theme8-gallery-small">

          <img
            src={GALLERY_IMAGE_1}
            alt="Indian food"
          />

        </div>


        <div className="theme8-gallery-small">

          <img
            src={GALLERY_IMAGE_2}
            alt="Indian curry"
          />

        </div>


        <div className="theme8-gallery-copy">

          <span className="theme8-eyebrow">
            OUR GALLERY
          </span>

          <h2>
            Moments of
            <strong>
              Good Food
            </strong>
          </h2>

          <div className="theme8-small-line" />

          <p>
            From our kitchen to your
            table, every moment is
            filled with flavour,
            tradition and happiness.
          </p>

        </div>

      </section>


      {/* =====================================================
          FULL MENU
      ====================================================== */}

      <section
        id="theme8-menu"
        className="theme8-menu"
      >

        <div className="theme8-menu-heading">

          <span className="theme8-eyebrow">
            FROM OUR KITCHEN
          </span>

          <h2>
            Explore Our
            <strong>
              Menu
            </strong>
          </h2>

          <p>
            Every dish is prepared fresh
            and served with authentic
            Indian flavour.
          </p>

        </div>


        <div className="theme8-menu-layout">


          {/* CATEGORY NAVIGATION */}

          <aside className="theme8-menu-sidebar">

            {sortedCategories.map(
              (category) => (

                <button
                  type="button"
                  key={category.id}
                  className={
                    activeCategory ===
                    category.id
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setActiveCategory(
                      category.id
                    )
                  }
                >

                  <span>
                    {category.name}
                  </span>

                  <small>
                    {
                      category.items
                        ?.length || 0
                    }
                  </small>

                </button>

              )
            )}

          </aside>


          {/* MENU ITEMS */}

          <div className="theme8-menu-items">

            {sortedCategories.map(
              (category) => {

                if (
                  activeCategory !== null &&
                  activeCategory !==
                    category.id
                ) {
                  return null;
                }


                return (

                  <div
                    className="theme8-menu-category"
                    key={category.id}
                  >

                    <div className="theme8-menu-category-heading">

                      <span>
                        {category.name}
                      </span>

                      <div />

                    </div>


                    <div className="theme8-menu-list">

                      {category.items.map(
                        (item) => {

                          const image =
                            getImageUrl(
                              item.image
                            );


                          return (

                            <article
                              className="theme8-menu-item"
                              key={item.id}
                            >

                              <div className="theme8-menu-item-image">

                                {image ? (

                                  <img
                                    src={image}
                                    alt={
                                      item.name
                                    }
                                  />

                                ) : (

                                  <Utensils />

                                )}

                              </div>


                              <div className="theme8-menu-item-details">

                                <div>

                                  <h3>
                                    {item.name}
                                  </h3>

                                  <p>
                                    {
                                      item.description
                                    }
                                  </p>

                                </div>


                                <div className="theme8-menu-item-action">

                                  <strong>
                                    ₹
                                    {Number(
                                      item.price ||
                                        0
                                    ).toFixed(0)}
                                  </strong>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddToCart(
                                        item
                                      )
                                    }
                                  >

                                    <Plus
                                      size={17}
                                    />

                                    <span>
                                      Add
                                    </span>

                                  </button>

                                </div>

                              </div>

                            </article>

                          );

                        }
                      )}

                    </div>

                  </div>

                );

              }
            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="theme8-final">

        <div className="theme8-final-inner">

          <span>
            YOUR TABLE IS WAITING
          </span>

          <h2>
            Hungry?
            <strong>
              Let's fix that.
            </strong>
          </h2>

          <p>
            Discover bold Indian flavours
            prepared fresh for you.
          </p>

          <button
            type="button"
            className="theme8-final-button"
            onClick={scrollToMenu}
          >
            Start Your Order
            <ArrowRight size={19} />
          </button>

        </div>

      </section>


      {/* =====================================================
          FLOATING CART
      ====================================================== */}

      {cartCount > 0 && (

        <button
          type="button"
          className="theme8-floating-cart"
          onClick={() =>
            navigate("cart")
          }
        >

          <span>
            {cartCount}
          </span>

          <span>
            View Cart
          </span>

          <ArrowRight size={18} />

        </button>

      )}

    </main>
  );
}

export default HomeScreen;